import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { CreateDisclosureService, DSTTaxYear } from '../../statement-tracker/create-disclosure/create-new/create-disclosure.service';
import { Alert } from '../../statement-tracker/statement-tracker.service';
import { DiagnosticsService } from '../diagnostics.service';
import { Subscription, take, zip } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { DSTStatement } from '../../shared/models/dst-statement.model';
import { TrackerTabsService } from '../../statement-tracker/tracker-tabs.service';
import { DSTDynamicComponentResolverService } from '../../statement-tracker/create-disclosure/shared/dst-dynamic-component-resolver.service';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { DSTSharedService } from '../../shared/dst-shared.service';
import { DSTChildFormState } from '../../shared/models/dst-child-form-state.model';
import { StmtIdBtnService } from '../../shared/components/stmt-id-btn/stmt-id-btn.service';
import { DiscModalViewerComponent } from '../../shared/components/disc-modal-viewer/disc-modal-viewer.component';
import { SaveActionResponse } from '../../../shared/_models/save-action-response.model';
import { JSONExportHeader, JSONExportService } from '../../../shared/_services/JSONExport.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { generalConfigFromGlobal, hydratedGlobalParams } from '../../../shared/workspace-element-context';
import { DstMatTableDataSource } from '../../shared/mat-table/dst-mat-table-data-source';
import { DstMatTableFilterApi } from '../../shared/mat-table/dst-mat-table-filter-api';
import {
  buildDisplayedColumns,
  connectMatTableSort,
  getDstColStyle,
} from '../../shared/mat-table/dst-mat-grid.util';

import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem
} from '../../shared/utils/dst-tax-year-appkit.support';
import { getAppkitDropdownSelection } from '../../../shared/utils/appkit-dropdown.util';

@Component({
  selector: 'gtw-data-collect-report',
  templateUrl: './data-collect-report.component.html',
  styleUrls: [
    '../../shared/styles/dst-shared.scss',
    '../../shared/mat-table/dst-mat-table.scss',
    './data-collect-report.component.scss',
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DataCollectReportComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(DiscModalViewerComponent) modalViewerComponent!: DiscModalViewerComponent;
  @ViewChild('otherExplanationModal') otherExplanationModal!: ModalComponent;
  @ViewChild(MatSort) sort!: MatSort;

  dataSource = new DstMatTableDataSource<any>([]);
  table: DstMatTableFilterApi<any>;
  displayedColumns: string[] = [];
  filterColumns: string[] = [];
  data: any[] = [];
  cols: any[] = [
    {
      field: 'TAXYEAR',
      header: 'Year',
      hidden: false,
      width: '60px'
    },
    {
      field: 'STATEMENTID',
      header: 'Stmt ID',
      hidden: false,
      width: '100px'
    },
    {
      field: 'STATEMENTNAME',
      header: 'Statement',
      hidden: false,
      width: '290px'
    },
    {
      field: 'DATACOLLECT_ACTION_FLAG',
      header: 'User Action Required',
      hidden: false,
      width: '125px'
    },
    {
      field: 'DATACOLLECT_ACTION_EXPL',
      header: 'Action Required',
      hidden: false,
      width: '280px'
    },
    {
      field: 'REVIEWNAME',
      header: 'Reviewer Explanation',
      hidden: false,
      width: '240px'
    },
    {
      field: 'reviewerOtherFieldExplanation',
      header: 'Reviewer Other Field Explanation',
      hidden: true,
    },
    {
      field: 'ISREVIEWED',
      header: 'Mark Statement as Reviewed',
      hidden: false,
      width: '130px'
    },
    {
      field: 'MODIFIEDBY',
      header: 'Modified By',
      hidden: true,
    },
    {
      field: 'MODIFIEDONFORMATTED',
      header: 'Modified On',
      hidden: true,
    },
    {
      field: 'CREATEDBY',
      header: 'Created By',
      hidden: true,
    },
    {
      field: 'CREATEDONFORMATTED',
      header: 'Created On',
      hidden: true,
    },
  ];
  loading: boolean = true;
  taxyearLoaded: boolean = false;
  excelExportFileName: string;
  taxYears: DSTTaxYear[] = [];
  taxYearItems: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = {};
  taxYear: DSTTaxYear | undefined;

  activeRow!: DSTStatement;
  showModalViewer: boolean = false;
  private onDisCancelSubs!: Subscription;
  alert: Alert;
  fStatementId: string = '';
  fStatementName: string = '';
  fActionFlag: string = '';
  fActionRequired: string = '';

  private contextApplied = false;

  revExplanations: any[] = [
    { label: 'Section Option', value: '', reviewTypeId: 0 },
  ];
  otherExplanationText: string = '';
  otherExplActiveRow: any;
  showOtherExplanationModal: boolean = false;

  constructor(private cDSerivce: CreateDisclosureService,
              public dstSharedService: DSTSharedService,
              private stmtIdBtnService: StmtIdBtnService,
              private trackerTabsService: TrackerTabsService,
              private diagnosticsService: DiagnosticsService,
              private jsonExportService: JSONExportService,
              private dCRService: DSTDynamicComponentResolverService,
              private gtwGlobal: GlobalService,
              private gtwWorkspace: WorkspaceService,
              private gtwApp: AppService) {

    this.excelExportFileName = 'Data_Collect_Diagnostics_' + this.dstSharedService.getExcelExportDateString();
    this.displayedColumns = buildDisplayedColumns(this.cols);
    this.filterColumns = this.displayedColumns.map((c) => `${c}_filter`);
    this.table = new DstMatTableFilterApi(this.dataSource);

    this.alert = dstSharedService.alert;
  }

  ngOnInit(): void {
    this.onDisCancelSubs = new Subscription();
    this.onDisCancelSubs.add(this.dCRService.onDisclosureCancel.subscribe(() => {
      this.trackerTabsService.destroyModalTab();
      this.showModalViewer = false;
    }));
    this.applyWorkspaceContext();
    this.onDisCancelSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngAfterViewInit(): void {
    connectMatTableSort(this.dataSource, this.sort);
  }

  ngOnDestroy(): void {
    this.onDisCancelSubs?.unsubscribe();
  }

  getColByField(field: string): { field: string; header: string; hidden?: boolean; width?: string } {
    return this.cols.find((c) => c.field === field)!;
  }

  getColStyle(col: { width?: string }): Record<string, string> {
    return getDstColStyle(col);
  }

  private applyWorkspaceContext(): void {
    if (this.contextApplied) return;
    try {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      const user = this.gtwApp.USER_SETTINGS?.user || {};
      const clientKey = String(user.client?.client_key || user.client_key || '');
      const ssoId = String(user.sso_id || '');
      const roleLevel = Number(this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level || 0);
      const taxYear = String(gp.tax_year || '');
      const scenario = String(gp.scenario || '');

      if (!clientKey && !ssoId) return;

      this.contextApplied = true;
      this.dstSharedService.setClientKey(clientKey);
      this.dstSharedService.setDSTUser(ssoId, roleLevel);
      this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);
      this.dstSharedService.setTaxYearScenario(taxYear, scenario);
      this.dstSharedService.setBaseURLs(gc);
      const getTaxYears$ = this.cDSerivce.getTaxYears();
      zip(getTaxYears$).pipe(take(1)).subscribe((data: any) => {
        this.taxYears = data[0];
        this.setTaxYear(taxYear);
        this.loadDiagReviewOptions();
        this.loadReport();
      });
    } catch (e) {
      if (e instanceof SyntaxError) {
        console.error('JSON parse Syntax Error' + e);
      }
      this.dstSharedService.showAlert('error', 'Missing gtw input params, please contact system administrator for assistance!');
    }
  }

  onTaxYearChange(): void {
    this.taxyearLoaded = true;
    this.loadReport();
  }

  setTaxYear(taxYear: string): void {
    this.taxYear = this.taxYears.find((item: any) => item.text.toString() === taxYear);
    this.taxYearItems = buildDstTaxYearItems(this.taxYears);
    this.selectedTaxYearItem = syncDstTaxYearItem(this.taxYearItems, this.taxYear, taxYear);
    this.taxyearLoaded = true;
  }

  onTaxYearChanged(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTaxYearItem = selected || this.selectedTaxYearItem;
    const matched = resolveDstTaxYearFromEvent(event, this.taxYears);
    if (matched) {
      this.taxYear = matched;
      this.onTaxYearChange();
    }
  }

  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  loadDiagReviewOptions(): void {
    this.diagnosticsService.getDiagnosticsReviewOptions('dataCollect').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      data.forEach((item: any) => {
        this.revExplanations.push({
          label: item.REVIEW_NAME,
          value: item.REVIEW_NAME,
          reviewTypeId: item.REVIEW_TYPE_ID,
        });
      });
    }, (error: HttpErrorResponse) => {
      console.error(error);
      this.dstSharedService.showAlert('error', 'Something went wrong while loading Review options, please try again!');
    }).add(() => {
    });
  }

  loadReport(): void {
    this.data = [];
    this.dataSource.data = [];
    this.loading = true;
    this.diagnosticsService.loadDataCollectReport(this.taxYear?.text.toString()!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.data = data;
      this.data.forEach((element, index) => {
        element.statementId = element.STATEMENTID;
        element.taxYearId = element.TAXYEARID;
        element.formId = element.FORMID;
        element.formName = element.FORMNAME;

        element.id = index + 1;
        element['revExplanations_' + element.id] = [];
        element['selectedExplanationItem'] = null;
        this.revExplanations.forEach((item: any) => {
          const isSelected = element.REVIEWNAME === item.value;
          const explanationItem = {
            label: item.label,
            value: item.value,
            reviewTypeId: item.reviewTypeId,
            selected: isSelected,
          };
          element['revExplanations_' + element.id].push(explanationItem);
          if (isSelected) {
            element['selectedExplanationItem'] = explanationItem;
          }
        });
      });
      this.dataSource.data = this.data;
      this.table.reset();
      this.resetColFilters();
      connectMatTableSort(this.dataSource, this.sort);
    }, (error: HttpErrorResponse) => {
      console.error(error);
      this.data = [];
      this.dataSource.data = [];
      this.dstSharedService.showAlert('error', 'Something went wrong while loading the Data Collect grid data, please try again!');
    }).add(() => {
      this.loading = false;
    });
  }

  showViewUpdateStatement(rowData: any): void {
    this.activeRow = rowData;
    this.dstSharedService.setTableActionStatus(true);
    this.dstSharedService.setIsFiled(rowData.IS_FILED!);
    this.stmtIdBtnService.getChildStatements(rowData.STATEMENTID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((childFormStates: DSTChildFormState[]) => {
      this.showModalViewer = true;
      setTimeout(() => {
        this.modalViewerComponent.showMore('dst-disclosure-viewer', childFormStates);
      });
    }, (eMsg: string) => {
      console.log(eMsg);
      this.dstSharedService.showAlert('error', eMsg);
    }).add(() => {
      this.dstSharedService.setTableActionStatus(false);
    });
  }

  afterModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showModalViewer = false;
  }

  filterCol(event: Event, field: string, matchMode: string): void {
    this.table.filter((event.target as HTMLInputElement).value, field, matchMode);
  }

  private resetColFilters(): void {
    this.fStatementId = '';
    this.fStatementName = '';
    this.fActionFlag = '';
    this.fActionRequired = '';
  }

  otherExplanation(rowData: any): void {
    this.showOtherExplanationModal = true;
    this.otherExplActiveRow = rowData;
    this.otherExplanationText = rowData.REVIEWEXPL || '';
    setTimeout(() => {
      this.otherExplanationModal.showModal('rev-other-exp');
    });
  }

  onOtherExplanationSave(id: string): void {
    this.otherExplActiveRow.REVIEWEXPL = this.otherExplanationText || '';

    this.updateOtherExplReviewComment(this.otherExplActiveRow.STATEMENTID, 'dataCollect', this.otherExplActiveRow.REVIEWTYPEID
      || '13', this.otherExplanationText || '');

    this.otherExplanationModal.closeModalOk(id);
    setTimeout(() => {
      this.showOtherExplanationModal = false;
    });
  }

  onOtherExplanationCancel(id: string): void {
    this.otherExplanationModal.closeModalOk(id);
    setTimeout(() => {
      this.showOtherExplanationModal = false;
    });
  }

  statementReviewedChange(event: any, rowData: any): void {
    rowData.markStatementAsReviewed = (event?.checkboxState ?? event?.srcElement?.checked ?? false) ? 1 : 0;
    this.stmtIdBtnService.setReviewed(rowData.statementId, rowData.markStatementAsReviewed).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: SaveActionResponse) => {
        if (response.callSuccess == 1) {
          this.dstSharedService.showAlert('success', 'Statement saved successfully for reviewed status.');
        } else {
          this.dstSharedService.showAlert('error', 'Something went wrong while reviewing status, please try again later!');
        }
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.dstSharedService.showAlert('error', error.message);
      });
  }

  reviewerExplanationChange(event: any, rowData: any): void {
    const selected = Array.isArray(event?.selected) ? event.selected : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    rowData.REVIEWNAME = change?.value;
    rowData.selectedExplanationItem = change;

    if (rowData.REVIEWNAME === 'Other: Explain') {
      this.otherExplanation(rowData);
    } else {
      rowData.REVIEWEXPL = '';
    }

    this.updateOtherExplReviewComment(rowData.STATEMENTID, 'dataCollect', change?.reviewTypeId, rowData.REVIEWEXPL || '');
  }

  updateOtherExplReviewComment(statementId: string, reviewType: string, reviewTypeId: string, otherExpl: string): void {
    this.diagnosticsService.dstUpdateReviewComment(statementId, reviewType, reviewTypeId, otherExpl).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: SaveActionResponse) => {
        if (response.callSuccess == 1) {
          this.dstSharedService.showAlert('success', 'Review Type Saved Successfully.');
        } else {
          this.dstSharedService.showAlert('error', 'Something went wrong while saving review type, please try again later!');
        }
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.dstSharedService.showAlert('error', error.message);
      });
  }

  exportToExcel(): void {
    const headersMap: JSONExportHeader[] = [
      {
        field: 'TAXYEAR',
        header: 'Year',
      },
      {
        field: 'STATEMENTID',
        header: 'Stmt ID',
      },
      {
        field: 'STATEMENTNAME',
        header: 'Statement',
      },
      {
        field: 'DATACOLLECT_ACTION_FLAG',
        header: 'User Action Required',
      },
      {
        field: 'DATACOLLECT_ACTION_EXPL',
        header: 'Action Required',
      },
      {
        field: 'REVIEWNAME',
        header: 'Reviewer Explanation',
      },
      {
        field: 'reviewerOtherFieldExplanation',
        header: 'Reviewer Other Field Explanation',
      },
      {
        field: 'ISREVIEWED',
        header: 'Mark Statement as Reviewed',
      },
      {
        field: 'MODIFIEDBY',
        header: 'Modified By',
      },
      {
        field: 'MODIFIEDONFORMATTED',
        header: 'Modified On',
      },
      {
        field: 'CREATEDBY',
        header: 'Created By',
      },
      {
        field: 'CREATEDONFORMATTED',
        header: 'Created On',
      }
    ];

    const exportData = Object.keys(this.table.filters).length > 0 ? this.table.filteredValue : this.data;
    this.jsonExportService.exportToExcel(this.excelExportFileName, exportData, headersMap);
  }

  setAriaLabel(item: any, contextLabel: string = 'Option'): string {
    const label = item?.label || item?.text || '-';
    return `${contextLabel} option ${label}`.trim();
  }

  onDropdownSelectItem(event: any): void {
  }

}
